// INTELIMOVE-SAAS-001 (IM-ADR-019): administração da plataforma, separada da administração do tenant.
// Papéis da plataforma NÃO são papéis de organização: nunca ampliam OWNER/ADMIN/MEMBER nem dão acesso a dados de tenant.
import { z } from 'zod';

export const PLATFORM_ROLES = ['PLATFORM_ADMIN', 'PLATFORM_SUPPORT'] as const;
export type PlatformRole = (typeof PLATFORM_ROLES)[number];
export const PLATFORM_ROLE_LABEL: Record<PlatformRole, string> = {
  PLATFORM_ADMIN: 'Administrador da plataforma',
  PLATFORM_SUPPORT: 'Suporte da plataforma',
};

/** Contexto ativo da sessão, validado no servidor a cada requisição (IM-SEC-105). */
export const SESSION_CONTEXTS = ['TENANT', 'PLATFORM'] as const;
export type SessionContext = (typeof SESSION_CONTEXTS)[number];

/**
 * IM-SEC-106: matriz de permissões da plataforma. Toda rota `/api/v1/platform/*` declara UMA permissão desta lista; rota
 * sem permissão declarada impede a API de subir (nasce bloqueada). Permissão nova começa vazia até decisão explícita.
 */
export const PLATFORM_PERMISSIONS = {
  'platform.overview.read': ['PLATFORM_ADMIN', 'PLATFORM_SUPPORT'],
  'platform.organizations.read': ['PLATFORM_ADMIN', 'PLATFORM_SUPPORT'],
  'platform.health.read': ['PLATFORM_ADMIN', 'PLATFORM_SUPPORT'],
  'platform.admins.read': ['PLATFORM_ADMIN', 'PLATFORM_SUPPORT'],
  'platform.admins.revoke': ['PLATFORM_ADMIN'],
  'platform.audit.read': ['PLATFORM_ADMIN'],
  // INTELIMOVE-SAAS-002 (IM-ADR-020): operações. Toda escrita é só do PLATFORM_ADMIN; o suporte continua só leitura.
  'platform.organizations.manage': ['PLATFORM_ADMIN'],
  'platform.admins.grant': ['PLATFORM_ADMIN'],
  'platform.admins.mfa': ['PLATFORM_ADMIN'],
  'platform.sessions.read': ['PLATFORM_ADMIN', 'PLATFORM_SUPPORT'],
  'platform.sessions.revoke': ['PLATFORM_ADMIN'],
  // INTELIMOVE-SAAS-003 (IM-ADR-021): metadados dos usuários de uma organização (suporte com e-mail mascarado) e
  // operações assistidas sobre vínculos e convites (só administrador, com reautenticação, motivo e confirmação).
  'platform.members.read': ['PLATFORM_ADMIN', 'PLATFORM_SUPPORT'],
  'platform.members.manage': ['PLATFORM_ADMIN'],
  // INTELIMOVE-SAAS-004 (IM-ADR-022): comunicação por e-mail. Suporte só lê a situação operacional (sem conexão, sem
  // destinatário completo); configurar, testar, ligar/desligar, reprocessar e pedir recuperação assistida é do administrador.
  'platform.mail.read': ['PLATFORM_ADMIN', 'PLATFORM_SUPPORT'],
  'platform.mail.manage': ['PLATFORM_ADMIN'],
  'platform.access.recovery': ['PLATFORM_ADMIN'],
  // INTELIMOVE-SAAS-005 (IM-ADR-023): comercial. Suporte consulta catálogo, assinaturas e consumo (metadados, sem pessoa);
  // razão de créditos e toda escrita (planos, assinaturas, créditos) são só do administrador, com reautenticação.
  'platform.commercial.read': ['PLATFORM_ADMIN', 'PLATFORM_SUPPORT'],
  'platform.commercial.ledger': ['PLATFORM_ADMIN'],
  'platform.billing.manage': ['PLATFORM_ADMIN'],
  'platform.plans.manage': ['PLATFORM_ADMIN'],
  'platform.subscriptions.manage': ['PLATFORM_ADMIN'],
  'platform.credits.manage': ['PLATFORM_ADMIN'],
  // SAAS-007: leitura da prontidão para ADMIN/SUPPORT; a ativação pública é exclusiva de ADMIN e exige step-up.
  'platform.commercial.activate': ['PLATFORM_ADMIN'],
  /** Segurança da própria conta (fator, códigos de recuperação): cada um só a sua. */
  'platform.self': ['PLATFORM_ADMIN', 'PLATFORM_SUPPORT'],
} as const satisfies Record<string, readonly PlatformRole[]>;
export type PlatformPermission = keyof typeof PLATFORM_PERMISSIONS;
export const platformCan = (role: PlatformRole | null | undefined, permission: PlatformPermission): boolean =>
  !!role && (PLATFORM_PERMISSIONS[permission] as readonly PlatformRole[]).includes(role);

/** Elegibilidade do próprio usuário (sempre a dele; nunca a de outra pessoa). */
export interface PlatformEligibility {
  role: PlatformRole;
  /** Fator TOTP cadastrado pelo operador. Sem ele, não há entrada no contexto administrativo. */
  mfaEnrolled: boolean;
  accountKind: 'CUSTOMER' | 'PLATFORM';
}

const TotpCode = z.string().trim().regex(/^\d{6}$/, 'Informe os 6 dígitos do aplicativo autenticador.');
const Reason = z.string().trim().min(5, 'Explique o motivo (mínimo de 5 caracteres).').max(500);

/**
 * IM-API-355: entrada explícita no contexto administrativo. Segundo fator obrigatório: código do aplicativo OU um código
 * de recuperação de uso único (SAAS-002). Nunca os dois, nunca nenhum.
 */
export const EnterPlatformRequest = z.union([
  z.object({ code: TotpCode }).strict(),
  z.object({ recoveryCode: z.string().trim().min(10).max(24) }).strict(),
]);

/**
 * IM-API-362: revogação de papel da plataforma (PLATFORM_ADMIN). SAAS-002: operação sensível — exige código novo do
 * aplicativo autenticador (reautenticação).
 */
export const RevokePlatformGrantRequest = z.object({ reason: Reason, code: TotpCode }).strict();

/** IM-API-366/367: suspender/reativar. `confirm` = identificador (slug) da organização, digitado. */
export const OrganizationStatusRequest = z.object({ reason: Reason, confirm: z.string().trim().min(1).max(120), code: TotpCode }).strict();

/** IM-API-369: concessão pela interface a uma identidade existente (nunca a si mesmo). */
export const GrantPlatformRoleRequest = z.object({
  userId: z.string().uuid(), role: z.enum(PLATFORM_ROLES), reason: Reason, code: TotpCode, confirm: z.literal(true),
}).strict();

/** IM-API-370: redefinição do segundo fator de outra pessoa. */
export const ResetPlatformMfaRequest = z.object({ reason: Reason, code: TotpCode }).strict();

/** IM-API-372/373: encerramento de sessões administrativas. */
export const RevokePlatformSessionRequest = z.object({ reason: Reason }).strict();

/** IM-API-375: novos códigos de recuperação (reautenticação com o aplicativo). */
export const RegenerateRecoveryCodesRequest = z.object({ code: TotpCode }).strict();

/** IM-API-376: recadastro do próprio aplicativo autenticador (reautenticação com a senha). */
export const RebindPlatformMfaRequest = z.object({ password: z.string().min(1).max(200) }).strict();

/** IM-API-377: início do cadastro com o código entregue fora de banda. */
export const StartPlatformEnrollmentRequest = z.object({ ticket: z.string().trim().min(16).max(64) }).strict();

/** IM-API-378: conclusão do cadastro (código do aplicativo recém-configurado). */
export const ConfirmPlatformEnrollmentRequest = z.object({ enrollmentId: z.string().uuid(), code: TotpCode }).strict();

export const PLATFORM_ORGANIZATION_SORTS = ['created', 'name', 'members', 'updated', 'activity'] as const;

export const PlatformOrganizationsQuery = z.object({
  q: z.string().trim().max(120).optional(),
  status: z.enum(['ACTIVE', 'SUSPENDED']).optional(),
  purpose: z.enum(['REAL', 'TECNICO', 'DEMONSTRACAO']).optional(),
  sort: z.enum(PLATFORM_ORGANIZATION_SORTS).default('created'),
  dir: z.enum(['asc', 'desc']).default('desc'),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  offset: z.coerce.number().int().min(0).max(100_000).default(0),
}).strict();

export interface PlatformOverviewDto {
  organizations: { total: number; active: number; suspended: number; real: number; technical: number; demonstration: number; createdLast30Days: number };
  /** Situação registrada em `tenant_subscriptions` (legado, sem plano comercial). */
  subscriptions: Record<string, number>;
  users: { total: number; active: number; disabled: number; platformAccounts: number; linked: number; withoutOrganization: number };
  platform: { admins: number; support: number; mfaConfirmed: number; adminsWithMfa: number; withoutMfa: number; withoutRecovery: number; pendingEnrollments: number };
  sessions: { activeTenant: number; activePlatform: number; platformByRecovery: number };
  /** Trabalho retido de organizações suspensas (fica na fila, volta ao reativar). */
  held: { jobs: number; organizations: number };
  workers: { live: number; registered: number };
  health: PlatformHealthDto;
  /** Grupos de possíveis duplicidades com confiança ALTA (diagnóstico; nada é consolidado). */
  duplicateGroups: number;
  alerts: PlatformAlert[];
}

export interface PlatformAlert {
  code: string;
  level: 'critical' | 'warning' | 'info';
  message: string;
  /** Rota da administração onde o alerta se resolve. */
  to?: string;
}

export interface PlatformHealthDto {
  version: string;
  database: boolean;
  schema: { expected: string | null; applied: string | null; ok: boolean };
  worker: { ok: boolean; lastHeartbeatAgeSeconds: number | null };
  queue: { queued: number; running: number; dead: number };
  queueByKind: Array<{ kind: string; queued: number; running: number; dead: number }>;
  /** API respondeu (esta própria resposta) — versão e tempo no ar do processo. */
  api: { ok: true; uptimeSeconds: number };
  workers: { live: number; registered: number };
  held: Array<{ kind: string; jobs: number; organizations: number }>;
}

export interface PlatformOrganizationRow {
  id: string;
  slug: string;
  name: string;
  purpose: 'REAL' | 'TECNICO' | 'DEMONSTRACAO';
  operationalStatus: 'ACTIVE' | 'SUSPENDED';
  subscriptionStatus: string | null;
  planCode: string | null;
  members: number;
  owners: number;
  admins: number;
  ownerName: string | null;
  primaryDomain: string | null;
  createdAt: string;
  updatedAt: string;
  lastActivityAt: string | null;
}

export interface PlatformOrganizationDetailDto {
  id: string;
  slug: string;
  name: string;
  purpose: PlatformOrganizationRow['purpose'];
  operationalStatus: PlatformOrganizationRow['operationalStatus'];
  createdAt: string;
  updatedAt: string;
  lastActivityAt: string | null;
  registration: { legalName: string | null; tradeName: string | null; taxId: string | null; primaryDomain: string | null; country: string | null; region: string | null; city: string | null };
  subscription: { status: string; planCode: string | null; startedAt: string; currentPeriodEnd: string | null } | null;
  members: { total: number; owners: number; admins: number; members: number; disabled: number };
  pendingInvitations: number;
  administrators: Array<{ userId: string; name: string; email: string; role: 'OWNER' | 'ADMIN'; status: string; since: string }>;
  statusHistory: Array<{ from: 'ACTIVE' | 'SUSPENDED'; to: 'ACTIVE' | 'SUSPENDED'; reason: string; actorName: string | null; at: string }>;
}

/** IM-API-365: impacto de suspender (só contagens). */
export interface PlatformOrganizationImpactDto {
  members: number;
  membersWithOtherOrganizations: number;
  openSessions: number;
  queuedJobs: number;
  runningJobs: number;
  scheduledSources: number;
  activeSchedules: number;
  activeConnectors: number;
  pendingDeliveries: number;
}

/**
 * Campos que a administração da plataforma pode devolver sobre uma organização. Teste de contrato (IM-TEST-1410)
 * confere que a resposta não tem nada além disto — contexto comercial, pesquisas, planos e decisões ficam de fora.
 */
export const PLATFORM_ORGANIZATION_FIELDS = ['id', 'slug', 'name', 'purpose', 'operationalStatus', 'createdAt', 'updatedAt', 'lastActivityAt', 'registration', 'subscription', 'members', 'pendingInvitations', 'administrators', 'statusHistory'] as const;

export interface PlatformAdminDto {
  grantId: string;
  userId: string;
  name: string;
  email: string;
  accountKind: 'CUSTOMER' | 'PLATFORM';
  userStatus: string;
  role: PlatformRole;
  grantedVia: string;
  grantedByName: string | null;
  grantedAt: string;
  revokedAt: string | null;
  revokedVia: string | null;
  mfaEnrolled: boolean;
  mfaConfirmed: boolean;
  recoveryRemaining: number;
  enrollmentPending: boolean;
  enrollmentExpiresAt: string | null;
  lastPlatformEntryAt: string | null;
  lastPlatformActivityAt: string | null;
  openSessions: number;
}

/** IM-API-368: identidade encontrada por e-mail exato, com a elegibilidade calculada no servidor. */
export interface PlatformCandidateDto {
  userId: string;
  name: string;
  email: string;
  accountKind: 'CUSTOMER' | 'PLATFORM';
  userStatus: string;
  liveRole: PlatformRole | null;
  mfaEnrolled: boolean;
  organizations: number;
  eligible: boolean;
  /** Motivo da inelegibilidade (texto para a tela). */
  ineligibleReason: string | null;
}

/** IM-API-369/370: código de cadastro do segundo fator — aparece UMA vez para quem emitiu; entregue fora de banda. */
export interface PlatformEnrollmentTicketDto {
  ticket: string;
  expiresAt: string;
}

/** IM-API-371: sessão administrativa ativa (nunca o token). */
export interface PlatformSessionDto {
  sessionId: string;
  userId: string;
  name: string;
  role: PlatformRole | null;
  createdAt: string;
  lastSeenAt: string;
  expiresAt: string;
  ip: string | null;
  userAgent: string | null;
  mfaMethod: 'TOTP' | 'RECOVERY' | null;
  current: boolean;
}

/** IM-API-374: segurança da própria conta. */
export interface PlatformSecurityDto {
  factor: { enrolledVia: string; createdAt: string; confirmedAt: string | null; lastUsedAt: string | null } | null;
  recovery: { remaining: number; generatedAt: string | null };
  sessionMethod: 'TOTP' | 'RECOVERY' | null;
}

/** IM-API-376/377: início de cadastro — só para o próprio titular. `secret` para digitação manual no aplicativo. */
export interface PlatformEnrollmentStartDto {
  enrollmentId: string;
  otpauthUri: string;
  secret: string;
  expiresAt: string;
}

/** IM-API-375/378: códigos de recuperação — aparecem UMA vez; o servidor guarda só o HMAC. */
export interface PlatformRecoveryCodesDto {
  recoveryCodes: string[];
}

export interface PlatformAuditDto {
  id: number;
  action: string;
  actorName: string | null;
  targetType: string | null;
  targetId: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
}

/** Rótulos das ações da trilha administrativa (a interface não mostra o código cru). */
export const PLATFORM_AUDIT_LABEL: Record<string, string> = {
  'platform.context.entered': 'Entrou na administração',
  'platform.context.exited': 'Saiu da administração',
  'platform.context.denied': 'Entrada recusada',
  'platform.account.created': 'Conta administrativa criada',
  'platform.role.granted': 'Papel concedido',
  'platform.role.revoked': 'Papel revogado',
  'platform.mfa.enrolled': 'Segundo fator cadastrado',
  'platform.mfa.reset': 'Segundo fator redefinido',
  'platform.mfa.recovery_regenerated': 'Códigos de recuperação gerados',
  'platform.mfa.recovery_used': 'Entrada com código de recuperação',
  'platform.stepup.denied': 'Reautenticação recusada',
  'platform.organization.suspended': 'Organização suspensa',
  'platform.organization.reactivated': 'Organização reativada',
  'platform.session.revoked': 'Sessão administrativa encerrada',
  'platform.session.revoked_all': 'Sessões administrativas encerradas',
};

/** Validade do código de cadastro do segundo fator emitido por outro administrador. */
export const PLATFORM_ENROLLMENT_TICKET_HOURS = 72;
/** Quantidade de códigos de recuperação por geração. */
export const PLATFORM_RECOVERY_CODE_COUNT = 10;
